import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { persistTheme } from "../theme";
import { useLang } from "../i18n";

const links = [
  { to: "/#about", key: "about", id: "about" },
  { to: "/experience", key: "experience" },
  { to: "/projects", key: "projects" },
  { to: "/#skills", key: "skills", id: "skills" },
  { to: "/contact", key: "contact" },
];

function Header({ wide = false }) {
  const { theme, toggle } = useTheme();
  const { lang, toggle: toggleLang, t } = useLang();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  const goToSection = (event, id) => {
    if (pathname !== "/") return;
    event.preventDefault();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.getElementById(id)?.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "start",
    });
    navigate({ pathname: "/", hash: `#${id}` });
  };

  return (
    <header className="site-header sticky top-0 z-20">
      <div className={`mx-auto flex w-full flex-wrap items-center gap-x-4 gap-y-3 px-5 py-3 sm:px-8 ${wide ? "max-w-[1040px]" : "max-w-[760px]"}`}>
        <Link to="/" className="brand">
          <span className="text-accent">~/</span>ali
        </Link>
        <nav className="order-3 flex w-full items-center gap-4 sm:order-none sm:w-auto sm:flex-1 sm:justify-end sm:gap-5" aria-label={t.nav.label}>
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="nav-link"
              aria-current={link.to === pathname ? "page" : undefined}
              onClick={link.id ? (event) => goToSection(event, link.id) : undefined}
            >
              {t.nav[link.key]}
            </Link>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 sm:ml-0">
          <button
            type="button"
            className="pressable icon-btn lang-btn"
            aria-label={t.lang.switch}
            onClick={toggleLang}
          >
            {lang === "de" ? "en" : "de"}
          </button>
          <button
            type="button"
            className="pressable icon-btn"
            aria-label={isDark ? t.theme.toLight : t.theme.toDark}
            onClick={toggle}
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
          <a
            href="https://github.com/ali8600"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="GitHub"
            className="pressable icon-btn"
          >
            <GitHubIcon />
          </a>
          <a
            href="https://www.linkedin.com/in/aliidrees-cs/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="LinkedIn"
            className="pressable icon-btn"
          >
            <LinkedInIcon />
          </a>
        </div>
      </div>
    </header>
  );
}

function useTheme() {
  const [theme, setTheme] = useState(() => (
    document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark"
  ));

  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    persistTheme(next);
    setTheme(next);
  };

  return { theme, toggle };
}

function SunIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3.25" stroke="currentColor" strokeWidth="1.6" />
      <path stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" d="M12 3.5v1.8M12 18.7v1.8M3.5 12h1.8M18.7 12h1.8M6 6l1.3 1.3M16.7 16.7 18 18M18 6l-1.3 1.3M7.3 16.7 6 18" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <path stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" d="M15.5 3.8A8.2 8.2 0 1 0 20.2 15 6.6 6.6 0 0 1 15.5 3.8Z" />
    </svg>
  );
}

function GitHubIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
      <path d="M208.31,75.68A59.78,59.78,0,0,0,202.93,28,8,8,0,0,0,196,24a59.75,59.75,0,0,0-48,24H124A59.75,59.75,0,0,0,76,24a8,8,0,0,0-6.93,4,59.78,59.78,0,0,0-5.38,47.68A58.14,58.14,0,0,0,56,104v8a56.06,56.06,0,0,0,48.44,55.47A39.8,39.8,0,0,0,96,192v8H72a24,24,0,0,1-24-24A40,40,0,0,0,8,136a8,8,0,0,0,0,16,24,24,0,0,1,24,24,40,40,0,0,0,40,40H96v16a8,8,0,0,0,16,0V192a24,24,0,0,1,48,0v40a8,8,0,0,0,16,0V192a39.8,39.8,0,0,0-8.44-24.53A56.06,56.06,0,0,0,216,112v-8A58.14,58.14,0,0,0,208.31,75.68ZM200,112a40,40,0,0,1-40,40H112a40,40,0,0,1-40-40v-8a41.74,41.74,0,0,1,6.9-22.48A8,8,0,0,0,80,73.83a43.81,43.81,0,0,1,.79-33.58,43.88,43.88,0,0,1,32.32,20.06A8,8,0,0,0,119.82,64h32.35a8,8,0,0,0,6.74-3.69,43.87,43.87,0,0,1,32.32-20.06A43.81,43.81,0,0,1,192,73.83a8.09,8.09,0,0,0,1,7.65A41.72,41.72,0,0,1,200,104Z" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 256 256" aria-hidden="true">
      <path d="M216,24H40A16,16,0,0,0,24,40V216a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V40A16,16,0,0,0,216,24Zm0,192H40V40H216V216ZM96,112v64a8,8,0,0,1-16,0V112a8,8,0,0,1,16,0Zm88,28v36a8,8,0,0,1-16,0V140a20,20,0,0,0-40,0v36a8,8,0,0,1-16,0V112a8,8,0,0,1,15.79-1.78A36,36,0,0,1,184,140ZM100,84A12,12,0,1,1,88,72,12,12,0,0,1,100,84Z" />
    </svg>
  );
}

export default Header;
